import { ReducerCreators, PayloadAction } from '@reduxjs/toolkit';

import { AudiencesState } from './types';
import {
  DeletePasscodeList,
  GetPasscodeListMemberCount,
  GetPasscodeListMembers,
  GetPasscodeLists,
  SearchPasscodeListMembers,
} from '@/connection/audiences/passcode-lists';
import { PasscodeList } from '@/types/registration.types';
import {
  SearchPaidListMembers,
  GetPaidListMembers,
  GetPaidLists,
  AddPaidListMember,
} from '@/connection/audiences/paid-lists';
import { GetGlobalCurrencies } from '@/connection/integrations/stripe';

export const reducers = (create: ReducerCreators<AudiencesState>) => ({
  fetchPasscodeLists: create.asyncThunk(
    async (channel: number) => {
      return await GetPasscodeLists(channel);
    },
    {
      fulfilled: (state, action) => {
        if (action.payload) {
          state.passcodeLists = action.payload || [];
        }
        state.loadingPasscodeLists = false;
      },
      pending: (state) => {
        state.loadingPasscodeLists = true;
      },
      rejected: (state, action) => {
        state.loadingPasscodeLists = false;
        console.error(action.error);
      },
    }
  ),
  fetchPasscodeListMembers: create.asyncThunk(
    async ({ passcodeListId, offset, order }: { passcodeListId: number; offset: number; order: number }) => {
      const passcodeListMembers = await GetPasscodeListMembers(passcodeListId, offset, order);
      return passcodeListMembers;
    },
    {
      fulfilled: (state, action) => {
        if (action.payload) {
          state.passcodeListMembers = action.payload || [];
        }
        state.loadingPasscodeListMembers = false;
      },
      pending: (state) => {
        state.loadingPasscodeListMembers = true;
      },
      rejected: (state, action) => {
        state.loadingPasscodeListMembers = false;
        console.error(action.error);
      },
    }
  ),
  fetchPasscodeListMemberCount: create.asyncThunk(
    async ({ passcodeListId, search }: { passcodeListId: number; search?: string }) => {
      const passcodeListMembers = await GetPasscodeListMemberCount(passcodeListId, search);
      return passcodeListMembers;
    },
    {
      fulfilled: (state, action) => {
        if (action.payload) {
          state.passcodeListMemberCount = action.payload[0].member_count;
        }
      },
    }
  ),
  searchPasscodeListMembers: create.asyncThunk(
    async ({
      passcodeListId,
      offset,
      order,
      search,
    }: {
      passcodeListId: number;
      offset: number;
      order: number;
      search: string;
    }) => {
      const passcodeListMembers = await SearchPasscodeListMembers(passcodeListId, offset, order, search);
      return passcodeListMembers;
    },
    {
      fulfilled: (state, action) => {
        if (action.payload) {
          state.passcodeListMembers = action.payload || [];
        }
        state.loadingPasscodeListMembers = false;
      },
      pending: (state) => {
        state.loadingPasscodeListMembers = true;
      },
      rejected: (state, action) => {
        state.loadingPasscodeListMembers = false;
        console.error(action.error);
      },
    }
  ),
  deletePasscodeList: create.asyncThunk(
    async ({ channel, list }: { channel: number; list: PasscodeList }) => {
      return await DeletePasscodeList(channel, list);
    },
    {
      fulfilled: (state, action) => {
        state.passcodeLists = state.passcodeLists.filter((list: PasscodeList) => {
          return list.passcode_list !== action?.payload?.listRemoved?.passcode_list;
        });
      },
      rejected: (state, action) => {
        state.loadingPasscodeLists = false;
        console.error(action.error);
      },
    }
  ),
  setShowAddPasscodeListModal: create.reducer((state, action: PayloadAction<boolean>) => {
    state.showAddPasscodeListModal = action.payload;
  }),
  fetchPaidLists: create.asyncThunk(
    async (channel: number) => {
      return await GetPaidLists(channel);
    },
    {
      fulfilled: (state, action) => {
        state.paidLists = action.payload;
      },
    }
  ),
  fetchGlobalCurrencies: create.asyncThunk(
    async () => {
      return await GetGlobalCurrencies();
    },
    {
      fulfilled: (state, action) => {
        state.globalCurrencies = action.payload;
      },
    }
  ),
  fetchPaidListMembers: create.asyncThunk(
    async ({ paidList, offset }: { paidList: string; offset: number }) => {
      return await GetPaidListMembers(paidList, offset);
    },
    {
      pending: (state) => {
        state.loadingPaidListMembers = true;
      },
      fulfilled: (state, action) => {
        state.paidListMembers = action.payload.members;
      },
      settled: (state) => {
        state.loadingPaidListMembers = false;
      },
    }
  ),
  searchPaidListMembers: create.asyncThunk(
    async ({ paidList, offset, search }: { paidList: string; offset: number; search: string }) => {
      return await SearchPaidListMembers(paidList, offset, search);
    },
    {
      pending: (state) => {
        state.loadingPaidListMembers = true;
      },
      fulfilled: (state, action) => {
        state.paidListMembers = action.payload;
      },
      settled: (state) => {
        state.loadingPaidListMembers = false;
      },
    }
  ),
  addPaidListMember: create.asyncThunk(
    async ({ paidList, email }: { paidList: string; email: string }) => {
      return await AddPaidListMember(paidList, email);
    },
    {
      fulfilled: (state, action) => {
        if (state.paidListMembers) {
          state.paidListMembers = [action.payload, ...state.paidListMembers];
        }
      },
    }
  ),
});
